/**
 * Ask the server whether the 3D asset library already holds an object.
 *
 * Matching lives on the server because it reads the whole `meshy_assets`
 * collection: there is no index on any name-like field, and shipping the library
 * to the browser to search it would grow with every asset ever made.
 */

import api from '../config/axios';

export interface LibraryMatch {
  /** `exact` is safe to reuse unasked; `fuzzy` is a suggestion for the author. */
  kind: 'exact' | 'fuzzy';
  assetId: string;
  name: string;
  prompt: string;
  thumbnailUrl: string;
  glbUrl: string;
  score: number;
}

interface MatchResponse {
  success: boolean;
  data?: { matches?: Array<{ prompt: string; match: LibraryMatch | null }> };
}

/**
 * One match (or null) per prompt, in the order given.
 *
 * A failure here is never fatal: the caller falls back to generating, which is
 * what it would have done anyway before reuse existed.
 */
export async function matchPromptsAgainstLibrary(prompts: string[]): Promise<Array<LibraryMatch | null>> {
  const cleaned = prompts.map((p) => String(p || '').trim());
  if (cleaned.every((p) => !p)) return cleaned.map(() => null);

  try {
    const response = await api.post<MatchResponse>('/meshy/library/match', { prompts: cleaned });
    const matches = response.data?.data?.matches || [];
    return cleaned.map((prompt, i) => {
      const row = matches[i];
      return row && row.prompt === prompt ? row.match ?? null : null;
    });
  } catch (error) {
    console.warn('[assetLibraryMatch] Library lookup failed; falling back to generation:', error);
    return cleaned.map(() => null);
  }
}

/** Convenience for the single-prompt call sites (manual add, pre-generation guard). */
export async function matchPromptAgainstLibrary(prompt: string): Promise<LibraryMatch | null> {
  const [match] = await matchPromptsAgainstLibrary([prompt]);
  return match ?? null;
}
